import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { de } from "date-fns/locale";
import { Users, Bed, Ruler, Gauge, Check, ArrowLeft } from "lucide-react";
import { api, fmtEUR, fmtEURPrecise, nightsBetween, toISODate } from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [range, setRange] = useState(undefined);
  const [extras, setExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [step, setStep] = useState(1); // 1: dates+extras, 2: details+pay
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setVehicle(r.data)).catch(() => navigate("/fahrzeuge"));
    api.get(`/vehicles/${id}/booked-dates`).then((r) => setBlocked(r.data.blocked_dates || []));
    api.get(`/extras`).then((r) => setExtras(r.data));
  }, [id, navigate]);

  useSEO({
    title: vehicle ? `${vehicle.name} mieten ab ${vehicle.price_per_day} €/Tag · Caravan Reisen` : "Reisemobil Details · Caravan Reisen",
    description: vehicle
      ? `${vehicle.name} (${vehicle.category}, ${vehicle.beds} Schlafplätze, ${vehicle.seats} Sitzplätze) online mieten. ${vehicle.description.slice(0, 110)}`
      : "Reisemobil-Details, Ausstattung und Verfügbarkeit bei Caravan Reisen.",
    path: `/fahrzeuge/${id}`,
    image: vehicle?.images?.[0],
  });

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return nightsBetween(range.from, range.to);
  }, [range]);

  const extrasTotal = useMemo(() => {
    return extras
      .filter((e) => selectedExtras.includes(e.id))
      .reduce((sum, e) => sum + (e.per_night ? e.price * nights : e.price), 0);
  }, [selectedExtras, extras, nights]);

  const subtotal = vehicle ? vehicle.price_per_day * nights : 0;
  const total = subtotal + extrasTotal;

  const blockedDates = blocked.map((s) => new Date(s + "T12:00:00"));
  const today = new Date(); today.setHours(0, 0, 0, 0);

  if (!vehicle) return <div className="pt-40 text-center">Laden...</div>;

  const toggleExtra = (id) => {
    setSelectedExtras((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const proceedToDetails = () => {
    setError("");
    if (!range?.from || !range?.to) { setError("Bitte Zeitraum wählen"); return; }
    if (nights < 1) { setError("Mindestens 1 Nacht"); return; }
    setStep(2);
  };

  const submitBooking = async () => {
    setError("");
    if (!customer.customer_name || !customer.customer_email || !customer.customer_phone) {
      setError("Bitte alle Kundendaten ausfüllen");
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await api.post("/bookings", {
        vehicle_id: vehicle.id,
        start_date: toISODate(range.from),
        end_date: toISODate(range.to),
        extras: selectedExtras,
        ...customer,
      });
      const booking = bookingRes.data;
      const checkoutRes = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Buchung fehlgeschlagen");
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-10" data-testid="vehicle-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#1B2A22]/70 hover:text-[#C05C42] mb-6" data-testid="back-btn">
        <ArrowLeft size={16} /> Zurück
      </button>

      <div className="grid lg:grid-cols-[1fr_420px] gap-10">
        {/* LEFT: Gallery + Info */}
        <div>
          <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-[#E8E6E1]">
            <img src={vehicle.images?.[activeImg]} alt={vehicle.name} className="w-full h-full object-cover" />
          </div>
          {vehicle.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-3">
              {vehicle.images.slice(0, 4).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-[4/3] rounded-lg overflow-hidden border-2 ${i === activeImg ? "border-[#C05C42]" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-10">
            <span className="chip">{vehicle.category}</span>
            <h1 className="section-title mt-4 text-4xl md:text-5xl">{vehicle.name}</h1>
            <p className="mt-4 text-[#1B2A22]/75 text-lg leading-relaxed max-w-2xl">{vehicle.description}</p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Users, l: "Sitzplätze", v: vehicle.seats },
                { icon: Bed, l: "Schlafplätze", v: vehicle.beds },
                { icon: Ruler, l: "Länge", v: `${vehicle.length_m} m` },
                { icon: Gauge, l: "Getriebe", v: vehicle.transmission },
              ].map((s, i) => (
                <div key={i} className="p-4 bg-white rounded-xl border border-[#D8D6D0]">
                  <s.icon size={18} className="text-[#C05C42]" />
                  <div className="mt-2 text-xs text-[#1B2A22]/60">{s.l}</div>
                  <div className="font-semibold">{s.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <h3 className="font-display font-semibold text-xl mb-4">Ausstattung</h3>
              <div className="grid grid-cols-2 gap-3">
                {vehicle.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-[#1B2A22]/80">
                    <Check size={16} className="text-[#A9BA9D]" /> {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Booking Widget */}
        <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-[#D8D6D0] p-6 shadow-[0_8px_30px_rgba(27,42,34,0.06)]" data-testid="booking-widget">
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-display font-bold text-[#C05C42]">{fmtEUR(vehicle.price_per_day)}</span>
            <span className="text-sm text-[#1B2A22]/60">/ Tag</span>
          </div>

          <div className="flex items-center gap-2 mb-4 text-xs">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold ${step === 1 ? "bg-[#1B2A22] text-white" : "bg-[#A9BA9D] text-white"}`}>1</div>
            <span className={step === 1 ? "font-semibold" : ""}>Zeitraum</span>
            <div className="flex-1 h-px bg-[#D8D6D0]"/>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold ${step === 2 ? "bg-[#1B2A22] text-white" : "bg-[#E8E6E1] text-[#1B2A22]"}`}>2</div>
            <span className={step === 2 ? "font-semibold" : ""}>Bezahlung</span>
          </div>

          {step === 1 && (
            <>
              <div className="border border-[#D8D6D0] rounded-xl p-2">
                <DayPicker
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  disabled={[{ before: today }, ...blockedDates.map((d) => ({ from: d, to: d }))]}
                  locale={de}
                  numberOfMonths={1}
                  data-testid="date-picker"
                />
              </div>

              {nights > 0 && (
                <div className="mt-4 text-sm text-[#1B2A22]/75" data-testid="nights-summary">
                  {nights} {nights === 1 ? "Nacht" : "Nächte"} · {fmtEURPrecise(subtotal)}
                </div>
              )}

              <div className="mt-6">
                <div className="label">Extras</div>
                <div className="space-y-2">
                  {extras.map((e) => (
                    <label key={e.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#D8D6D0] cursor-pointer hover:border-[#1B2A22]" data-testid={`extra-${e.id}`}>
                      <input
                        type="checkbox"
                        checked={selectedExtras.includes(e.id)}
                        onChange={() => toggleExtra(e.id)}
                        className="w-4 h-4 accent-[#C05C42]"
                      />
                      <div className="flex-1 text-sm">
                        <div className="font-medium">{e.label}</div>
                        <div className="text-xs text-[#1B2A22]/60">{fmtEURPrecise(e.price)} {e.per_night ? "/ Nacht" : "einmalig"}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {nights > 0 && (
                <div className="mt-6 pt-6 border-t border-[#D8D6D0] space-y-2 text-sm">
                  <div className="flex justify-between"><span>Grundpreis ({nights} Nächte)</span><span>{fmtEURPrecise(subtotal)}</span></div>
                  {extrasTotal > 0 && <div className="flex justify-between"><span>Extras</span><span>{fmtEURPrecise(extrasTotal)}</span></div>}
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-[#D8D6D0]"><span>Gesamt</span><span>{fmtEURPrecise(total)}</span></div>
                </div>
              )}

              {error && <div className="mt-4 text-sm text-[#C05C42]" data-testid="booking-error">{error}</div>}
              <button
                onClick={proceedToDetails}
                className="btn-primary w-full mt-6 justify-center"
                disabled={!range?.from || !range?.to || nights < 1}
                data-testid="proceed-to-details"
              >
                Weiter zur Buchung
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="p-4 rounded-xl bg-[#F0EFEA] text-sm space-y-1">
                <div className="flex justify-between"><span>Zeitraum</span><span>{toISODate(range.from)} bis {toISODate(range.to)}</span></div>
                <div className="flex justify-between"><span>{nights} Nächte</span><span>{fmtEURPrecise(subtotal)}</span></div>
                {extrasTotal > 0 && <div className="flex justify-between"><span>Extras</span><span>{fmtEURPrecise(extrasTotal)}</span></div>}
                <div className="flex justify-between font-bold pt-2 border-t border-[#D8D6D0]"><span>Gesamt</span><span>{fmtEURPrecise(total)}</span></div>
              </div>

              <div className="mt-5 space-y-3">
                <div>
                  <label className="label">Vollständiger Name</label>
                  <input className="input-field" data-testid="input-name" value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })} placeholder="Max Mustermann" />
                </div>
                <div>
                  <label className="label">E-Mail</label>
                  <input type="email" className="input-field" data-testid="input-email" value={customer.customer_email} onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })} placeholder="max@example.de" />
                </div>
                <div>
                  <label className="label">Telefonnummer</label>
                  <input className="input-field" data-testid="input-phone" value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} placeholder="+49 ..." />
                </div>
                <div>
                  <label className="label">Anmerkungen (optional)</label>
                  <textarea className="input-field" rows={2} data-testid="input-notes" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} />
                </div>
              </div>

              {error && <div className="mt-4 text-sm text-[#C05C42]" data-testid="checkout-error">{error}</div>}

              <div className="mt-6 flex gap-3">
                <button onClick={() => setStep(1)} className="btn-outline flex-1 justify-center" data-testid="back-to-step1">Zurück</button>
                <button onClick={submitBooking} disabled={submitting} className="btn-primary flex-[2] justify-center" data-testid="submit-checkout">
                  {submitting ? "Wird bearbeitet..." : `Jetzt ${fmtEUR(total)} zahlen`}
                </button>
              </div>
              <p className="mt-3 text-xs text-[#1B2A22]/60 text-center">Sichere Bezahlung über Stripe · SSL-verschlüsselt</p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
